import { useCallback, useEffect, useState, useRef } from 'react';

export interface GoogleAuthConfig {
  clientId: string;
  scope?: string;
}

interface GoogleUser {
  id: string;
  name: string;
  email: string;
  picture?: string;
  given_name?: string;
  family_name?: string;
}

interface UseGoogleAuthProps {
  clientId: string;
  onSuccess?: (user: GoogleUser, idToken: string) => void;
  onError?: (error: any) => void;
}

interface UseGoogleAuthReturn {
  isLoaded: boolean;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  error: string | null;
}

// Extend Window interface for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          cancel: () => void;
        };
        oauth2?: {
          initCodeClient: (config: any) => {
            requestCode: () => void;
          };
          initTokenClient: (config: any) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  }
}

/**
 * Decode JWT token to extract user information
 */
function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

export function useGoogleAuth({
  clientId,
  onSuccess,
  onError,
}: UseGoogleAuthProps): UseGoogleAuthReturn {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const callbackRef = useRef<((response: any) => void) | null>(null);
  const hiddenButtonRef = useRef<HTMLDivElement | null>(null);

  // Handle credential response (ID token)
  const handleCredentialResponse = useCallback((response: any) => {
    console.log('Google Sign-In: Credential response received');
    
    if (response?.error) {
      const errorMsg = `Google Sign-In error: ${response.error}`;
      setError(errorMsg);
      setIsLoading(false);
      onError?.(new Error(errorMsg));
      return;
    }

    const credential = response?.credential;
    if (!credential) {
      const errorMsg = 'No credential received from Google';
      setError(errorMsg);
      setIsLoading(false);
      onError?.(new Error(errorMsg));
      return;
    }

    // Parse the JWT ID token to get user info
    const userInfo = parseJwt(credential);
    if (!userInfo || !userInfo.email) {
      const errorMsg = 'Failed to parse credential or missing user information';
      setError(errorMsg);
      setIsLoading(false);
      onError?.(new Error(errorMsg));
      return;
    }

    const user: GoogleUser = {
      id: userInfo.sub,
      name: userInfo.name || `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim(),
      email: userInfo.email,
      picture: userInfo.picture,
      given_name: userInfo.given_name,
      family_name: userInfo.family_name,
    };

    console.log('Google Sign-In: User authenticated', user.email);
    setError(null);
    setIsLoading(false);
    
    // Pass the ID token (JWT) to the callback
    onSuccess?.(user, credential);
  }, [onSuccess, onError]);

  // Store callback in ref for use in initialization
  useEffect(() => {
    callbackRef.current = handleCredentialResponse;
  }, [handleCredentialResponse]);

  // Load Google Identity Services script
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check if already loaded
    if (window.google?.accounts?.id) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        setIsLoaded(true);
      });
      return;
    }

    // Load the Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsLoaded(true);
    };
    script.onerror = () => {
      setError('Failed to load Google Identity Services');
    };

    document.head.appendChild(script);
  }, []);

  // Create hidden button container and initialize Google Sign-In
  useEffect(() => {
    if (!isLoaded || !window.google?.accounts?.id) return;
    if (!clientId || clientId.includes('your-google-client-id')) return;

    try {
      // Initialize Google Sign-In with popup mode
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: any) => {
          callbackRef.current?.(response);
        },
        ux_mode: 'popup', // Use popup window
        auto_select: false,
      });

      // Create a hidden container for the Google button
      if (!hiddenButtonRef.current) {
        const container = document.createElement('div');
        container.id = 'google-signin-hidden-container';
        container.style.cssText = `
          position: fixed;
          top: -9999px;
          left: -9999px;
          opacity: 0;
          pointer-events: none;
        `;
        document.body.appendChild(container);
        hiddenButtonRef.current = container;

        // Render the hidden Google Sign-In button
        window.google.accounts.id.renderButton(container, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
        });
      }

      console.log('Google Sign-In initialized with popup mode');
    } catch (err) {
      console.error('Failed to initialize Google Sign-In:', err);
      setError('Failed to initialize Google Sign-In');
    }

    return () => {
      // Cleanup hidden button on unmount
      if (hiddenButtonRef.current && document.body.contains(hiddenButtonRef.current)) {
        document.body.removeChild(hiddenButtonRef.current);
        hiddenButtonRef.current = null;
      }
    };
  }, [isLoaded, clientId]);

  const signInWithGoogle = useCallback(async () => {
    if (!isLoaded || !window.google?.accounts?.id) {
      const errorMsg = 'Google Identity Services not loaded';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    if (!clientId || clientId.includes('your-google-client-id')) {
      const errorMsg = 'Invalid Google Client ID';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setIsLoading(true);
    setError(null);

    return new Promise<void>((resolve, reject) => {
      let isResolved = false;
      let popupCheckInterval: NodeJS.Timeout | null = null;
      let popupStartTime = Date.now();
      
      // Store original callback before setting up handlers
      const originalCallback = callbackRef.current;

      // Cleanup function
      const cleanup = () => {
        if (popupCheckInterval) {
          clearInterval(popupCheckInterval);
          popupCheckInterval = null;
        }
        window.removeEventListener('focus', handleWindowFocus);
      };

      // Handle window focus - when main window regains focus, popup might be closed
      const handleWindowFocus = () => {
        // Only check if popup was opened recently (within last 10 seconds)
        // This helps detect when popup closes but prevents false positives from tab switching
        const elapsed = Date.now() - popupStartTime;
        if (elapsed < 1000 || elapsed > 10000) {
          return; // Too soon (popup just opened) or too late (probably tab switching)
        }

        // Small delay to allow Google's callback to fire first if popup completed successfully
        setTimeout(() => {
          if (!isResolved) {
            // Check if we're still waiting - if so, popup was likely closed
            isResolved = true;
            cleanup();
            setIsLoading(false);
            setError(null);
            // Restore original callback
            callbackRef.current = originalCallback;
            reject(new Error('Popup was closed'));
          }
        }, 100);
      };

      // Listen for window focus events
      window.addEventListener('focus', handleWindowFocus);

      // Also use polling to check if popup is still open (fallback)
      // Check every 500ms if we're still waiting
      popupCheckInterval = setInterval(() => {
        if (!isResolved) {
          // If we've been waiting for more than 2 minutes, assume popup was closed
          const elapsed = Date.now() - popupStartTime;
          if (elapsed > 2 * 60 * 1000) {
            isResolved = true;
            cleanup();
            setIsLoading(false);
            setError(null);
            callbackRef.current = originalCallback;
            reject(new Error('Popup was closed or timed out'));
          }
        }
      }, 500);

      try {
        // Set up the callback to resolve/reject the promise
        callbackRef.current = (response: any) => {
          if (isResolved) return; // Already handled

          isResolved = true;
          cleanup();

          if (response?.credential) {
            originalCallback?.(response);
            resolve();
          } else if (response?.error) {
            setIsLoading(false);
            reject(new Error(response.error));
          } else {
            // No credential - user might have cancelled
            setIsLoading(false);
            originalCallback?.(response);
          }
          // Restore original callback
          callbackRef.current = originalCallback;
        };

        // Re-initialize to ensure fresh state
        window.google!.accounts!.id!.initialize({
          client_id: clientId,
          callback: (response: any) => {
            callbackRef.current?.(response);
          },
          ux_mode: 'popup',
          auto_select: false,
        });

        // Re-render the hidden button
        if (hiddenButtonRef.current) {
          hiddenButtonRef.current.innerHTML = '';
          window.google!.accounts!.id!.renderButton(hiddenButtonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
          });

          // Click the hidden button to trigger the popup
          setTimeout(() => {
            const googleButton = hiddenButtonRef.current?.querySelector('div[role="button"]') as HTMLElement;
            if (googleButton) {
              console.log('Triggering Google Sign-In popup...');
              googleButton.click();
            } else {
              // Fallback: try to find any clickable element
              const anyButton = hiddenButtonRef.current?.querySelector('iframe');
              if (anyButton) {
                // If it's an iframe, we need a different approach
                // Create a visible temporary button
                showTemporaryButton(resolve, reject);
              } else {
                setIsLoading(false);
                reject(new Error('Could not find Google Sign-In button'));
              }
            }
          }, 100);
        } else {
          setIsLoading(false);
          reject(new Error('Google Sign-In button not initialized'));
        }

      } catch (err) {
        console.error('Google Sign-In failed:', err);
        setIsLoading(false);
        setError('Google Sign-In failed. Please try again.');
        reject(err);
      }
    });
  }, [isLoaded, clientId]);

  // Fallback: show a temporary visible button
  const showTemporaryButton = useCallback((resolve: () => void, reject: (error: any) => void) => {
    const container = document.createElement('div');
    container.id = 'google-signin-temp-container';
    container.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10000;
      background: white;
      padding: 24px;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    `;

    const title = document.createElement('p');
    title.textContent = 'Click to sign in with Google';
    title.style.cssText = 'margin: 0 0 16px 0; text-align: center; font-family: system-ui, sans-serif;';
    container.appendChild(title);

    const buttonDiv = document.createElement('div');
    container.appendChild(buttonDiv);

    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      background: none;
      border: none;
      font-size: 18px;
      cursor: pointer;
      color: #666;
    `;

    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
    `;

    const cleanup = () => {
      if (document.body.contains(backdrop)) document.body.removeChild(backdrop);
      if (document.body.contains(container)) document.body.removeChild(container);
    };

    closeButton.onclick = () => {
      cleanup();
      setIsLoading(false);
      reject(new Error('User cancelled'));
    };
    backdrop.onclick = closeButton.onclick;
    container.appendChild(closeButton);

    // Override callback to include cleanup
    const originalCallback = callbackRef.current;
    callbackRef.current = (response: any) => {
      cleanup();
      if (response?.credential) {
        originalCallback?.(response);
        resolve();
      } else {
        setIsLoading(false);
        originalCallback?.(response);
      }
      callbackRef.current = originalCallback;
    };

    document.body.appendChild(backdrop);
    document.body.appendChild(container);

    window.google!.accounts!.id!.renderButton(buttonDiv, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: 280,
    });
  }, []);

  return {
    isLoaded,
    isLoading,
    signInWithGoogle,
    error,
  };
}
